// Sponsor-Ownership tag — who controls a company, and is it a PE sponsor?
//
// Surfaced as the Company Overview "Backing" field and fed into the
// Succession signal (a sponsor-/group-held platform is not a founder-
// succession opportunity). REFINES the existing ownership_type filter:
// sponsor-owned ⊂ "Corporate-owned (75%+)".
//
// THE DATA CONSTRAINT: the platform stores only the one-hop registrable
// corporate controller — `corporate_parent`, a NAME string. PE owns through
// holdco layers, and the dataset's biggest parents are overwhelmingly
// FAMILY-owned operating groups, so we resolve ownership two honest ways,
// never by guessing:
//   1. CURATED reference — the Corryk ownership names (sponsor-data.ts, 177
//      operators with register-style aliases → named backer + backer type +
//      confidence). A match is a registry-name match landing on a known
//      controlling entity. backer_type decides the verdict: pe/infra/sovereign
//      = a financial SPONSOR; strategic/listed/reit/foundation = trade/public/
//      landlord ownership; family/cooperative = owner-operated. We only call it
//      "Sponsor-backed" for the financial-sponsor types — listed/family/REIT
//      owners are reported as what they are, never as sponsors.
//   2. LBO-STRUCTURE heuristic — parent names matching leveraged-buyout
//      vehicle coinages (Bidco/Topco/Midco/Debtco/…) → "Likely sponsor-backed",
//      a flag, never a named claim. Plain "Holdings" excluded (family holdcos).
//
// Guardrail (from the reference): matching only; carry `confidence`; treat
// `low` as a lead, not a fact. Ownership is point-in-time (mid-2026).

import {
  SPONSOR_OPERATORS,
  SPONSOR_DATA_VERSION,
  type SponsorOperator,
  type BackerType,
  type OwnershipConfidence,
} from './sponsor-data.js';

export { SPONSOR_DATA_VERSION };
export type { BackerType, OwnershipConfidence, SponsorOperator };

export const SPONSOR_REFERENCE_REVIEW_BY = '2026-12-31';

/** Financial-sponsor backer types — the ones that earn "Sponsor-backed". */
const SPONSOR_TYPES: ReadonlySet<BackerType> = new Set<BackerType>(['pe', 'infra', 'sovereign']);

export type SponsorStatus = 'sponsor_backed' | 'group_owned' | 'likely_sponsor';

export interface SponsorResolution {
  status: SponsorStatus;
  basis: 'curated' | 'lbo_structure';
  /** Resolved platform/operator name (curated only). */
  operator: string | null;
  /** Named owner/sponsor; null when type known but fund unnamed, or heuristic. */
  backer: string | null;
  backerType: BackerType | null;
  confidence: OwnershipConfidence | null;
  sector: string | null;
}

// Registry-vehicle names the DB stores that don't match a brand alias (e.g. a
// numbered Cc/Bidco holdco). Hand-verified DB corporate_parent → operator name.
const EXTRA_HOLDCO_ALIASES: Readonly<Record<string, string>> = {
  'priory cc34': 'Priory Group',
};

/** Lowercase, drop legal suffixes + punctuation, collapse spaces. */
export function normaliseHoldco(name: string): string {
  return name
    .toLowerCase()
    .replace(/\b(limited|ltd|plc|llp|uk|group|holdings?)\b/g, ' ')
    .replace(/[^a-z0-9]+/g, ' ')
    .trim()
    .replace(/\s+/g, ' ');
}

// Build the normalised alias → operator lookup once. First write wins on the
// rare short-alias collision (both map to the same verdict anyway).
const OP_BY_NORM: ReadonlyMap<string, SponsorOperator> = (() => {
  const m = new Map<string, SponsorOperator>();
  for (const op of SPONSOR_OPERATORS) {
    for (const alias of op.aliases) {
      const n = normaliseHoldco(alias);
      if (n && !m.has(n)) m.set(n, op);
    }
  }
  const byName = new Map(SPONSOR_OPERATORS.map((o) => [o.name, o]));
  for (const [extra, opName] of Object.entries(EXTRA_HOLDCO_ALIASES)) {
    const op = byName.get(opName);
    if (op) m.set(normaliseHoldco(extra), op);
  }
  return m;
})();

// Recall pass: a DB registry name often carries extra tokens after the brand
// ("<Brand> Bidco Limited", "<Brand> (UK) Partnerships"), which exact-
// normalised match misses. We match when an alias's token phrase is a PREFIX
// of the parent name — brands lead registry names, and prefix-anchoring avoids
// matching a generic phrase buried mid-name ("Dumbledore Dental Care" must NOT
// hit Together Dental's "Dental Care"). Two guards keep precision high:
//   • aliases of ≥2 tokens only (single brand words like Priory/Smile/Adent
//     are too collision-prone and stay exact-only), and
//   • the phrase must carry ≥1 DISTINCTIVE token — an all-generic phrase
//     ("dental care", "oral care", "care management") is rejected outright.
// Longest phrase wins on overlap (most specific).
const GENERIC_TOKENS: ReadonlySet<string> = new Set([
  'care',
  'dental',
  'oral',
  'health',
  'healthcare',
  'services',
  'service',
  'senior',
  'living',
  'home',
  'homes',
  'medical',
  'clinic',
  'clinics',
  'nursing',
  'vet',
  'vets',
  'veterinary',
  'eye',
  'eyes',
  'optical',
  'pharmacy',
  'surgery',
  'practice',
  'practices',
  'partners',
  'partnership',
  'partnerships',
  'central',
  'community',
  'hospital',
  'hospitals',
  'physio',
  'physiotherapy',
  'specialist',
  'centre',
  'center',
  'the',
  'of',
  'and',
  'for',
  'at',
]);

interface AliasPhrase {
  tokens: string[];
  op: SponsorOperator;
}
const PHRASE_ALIASES: ReadonlyArray<AliasPhrase> = (() => {
  const seen = new Set<string>();
  const out: AliasPhrase[] = [];
  for (const op of SPONSOR_OPERATORS) {
    for (const alias of op.aliases) {
      const tokens = normaliseHoldco(alias).split(' ').filter(Boolean);
      if (tokens.length < 2) continue;
      if (!tokens.some((t) => !GENERIC_TOKENS.has(t))) continue; // all-generic → too risky
      const key = tokens.join(' ');
      if (seen.has(key)) continue; // first (alpha-sorted) op wins a shared phrase
      seen.add(key);
      out.push({ tokens, op });
    }
  }
  return out.sort(
    (a, b) =>
      b.tokens.length - a.tokens.length || a.tokens.join(' ').localeCompare(b.tokens.join(' ')),
  );
})();

/** True if `needle` is a leading prefix of `haystack`. */
function isPrefix(haystack: string[], needle: string[]): boolean {
  if (needle.length === 0 || needle.length > haystack.length) return false;
  for (let i = 0; i < needle.length; i++) {
    if (haystack[i] !== needle[i]) return false;
  }
  return true;
}

// Leveraged-buyout vehicle coinages — near-never accidental. "Holdings" is
// deliberately excluded (too many family holdcos).
const LBO_VEHICLE_RE = /\b(bid|mid|top|debt|hold|new|bond|fin|pik)co\b/i;

function classify(op: SponsorOperator): SponsorResolution {
  return {
    status: SPONSOR_TYPES.has(op.backerType) ? 'sponsor_backed' : 'group_owned',
    basis: 'curated',
    operator: op.name,
    backer: op.backer,
    backerType: op.backerType,
    confidence: op.confidence,
    sector: op.sector,
  };
}

/**
 * Resolve ownership from a company's one-hop corporate parent name.
 * Curated (named, type-classified) first, then the LBO heuristic, else null.
 */
export function resolveSponsor(
  corporateParent: string | null | undefined,
): SponsorResolution | null {
  if (!corporateParent || !corporateParent.trim()) return null;

  const norm = normaliseHoldco(corporateParent);

  // 1 · exact normalised match (highest precision).
  const exact = OP_BY_NORM.get(norm);
  if (exact) return classify(exact);

  // 2 · recall pass — a distinctive multi-token alias phrase leading the name.
  const tokens = norm.split(' ').filter(Boolean);
  for (const { tokens: phrase, op } of PHRASE_ALIASES) {
    if (isPrefix(tokens, phrase)) return classify(op);
  }

  // 3 · LBO-vehicle heuristic (unnamed).
  if (LBO_VEHICLE_RE.test(corporateParent)) {
    return {
      status: 'likely_sponsor',
      basis: 'lbo_structure',
      operator: null,
      backer: null,
      backerType: null,
      confidence: null,
      sector: null,
    };
  }

  return null;
}

/** Short chip label for a resolution — what kind of owner it is. */
export function backingLabel(r: SponsorResolution): string {
  if (r.status === 'sponsor_backed') return 'Sponsor-backed';
  if (r.status === 'likely_sponsor') return 'Likely sponsor-backed';
  switch (r.backerType) {
    case 'listed':
      return 'Listed-owned';
    case 'reit':
      return 'REIT-owned';
    case 'strategic':
      return 'Trade-owned';
    case 'foundation':
      return 'Foundation-owned';
    case 'cooperative':
      return 'Cooperative';
    case 'family':
    default:
      return 'Family-owned';
  }
}
